// packages/web-ui/src/sse/SseProvider.tsx
//
// W2-S5 AC #10 — React provider that manages the SSE stream lifecycle.
//
// Subscribes to the chat slice's `activeSessionId`. When it changes from
// `null → X` we open the SSE stream for that session; when it goes back to
// `null` (or to a different session) we abort and re-open.
//
// The fetchEventSource implementation is owned by `openSseStream` (client.ts)
// — this provider does NOT call the upstream library directly. Tests inject
// a fixture stream via the `fetchEventSourceImpl` prop.

import { useEffect, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { useRootStore } from '../state/store.js';
import { getDefaultApiContext } from '../api/client.js';
import {
  openSseStream,
  type FetchEventSourceImpl,
  type SseStreamHandle,
  type SseEvent,
} from './client.js';
import { dispatchSseEvent } from './attach-to-chat-slice.js';

export interface SseProviderProps {
  children?: ReactNode;
  /** Override fetch-event-source for tests. */
  fetchEventSourceImpl?: FetchEventSourceImpl;
  /** Override the SSE endpoint URL builder. */
  buildUrl?: (sessionId: string) => string;
}

/** Default URL builder — `/api/v1/chat/stream?sessionId=…`. */
export function defaultBuildSseUrl(sessionId: string): string {
  return `/api/v1/chat/stream?sessionId=${encodeURIComponent(sessionId)}`;
}

/** Cap on SPA-level reconnects per session. After this many consecutive
 *  reconnects without a successful `onOpen` we stop retrying and surface
 *  a disconnected state. Per-attempt = one fresh fetch-event-source
 *  instance with its own 10-retry internal budget, so this guards against
 *  the bump-token loop spawning new instances forever. */
const MAX_SPA_RECONNECTS = 3;

export function SseProvider({
  children,
  fetchEventSourceImpl,
  buildUrl,
}: SseProviderProps): JSX.Element {
  const activeSessionId = useRootStore((s) => s.activeSessionId);
  const handleRef = useRef<SseStreamHandle | null>(null);
  const lastSessionRef = useRef<string | null>(null);
  // Bumped whenever the SSE stream closes unexpectedly (server detached us
  // on backpressure, network blip, etc.). Including it in the effect deps
  // forces a re-open with the latest `lastEventId` so the SseHub's ring
  // buffer replays missed events. Without this the React useEffect only
  // re-runs on session change — a mid-session disconnect leaves the SPA
  // stranded forever waiting on a dead socket.
  const [reconnectToken, setReconnectToken] = useState(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Capped count of consecutive SPA-level reconnects since the last
  // successfully-opened stream. Resets to 0 on `onOpen`. After
  // MAX_SPA_RECONNECTS we stop retrying and surface a disconnected state.
  // Without a cap, the bump-token loop spawns fresh fetch-event-source
  // instances forever (each has its own internal retry budget that resets,
  // so the library's `onExhausted` never fires).
  const reconnectAttemptsRef = useRef<number>(0);

  useEffect(() => {
    // Tear down whatever stream is open when the active session changes.
    if (
      handleRef.current !== null &&
      lastSessionRef.current !== activeSessionId
    ) {
      handleRef.current.abort();
      handleRef.current = null;
    }

    if (activeSessionId === null) {
      lastSessionRef.current = null;
      // Slice transitions to disconnected — the sse slice action handles it.
      useRootStore.getState().markDisconnected();
      useRootStore.getState().setCurrentSession(null);
      return;
    }

    if (lastSessionRef.current === activeSessionId && handleRef.current !== null) {
      return; // already open
    }

    // Detect session switch BEFORE updating lastSessionRef so we can decide
    // whether the stored `lastEventId` cursor is still meaningful. The SseHub's
    // envelope id generator is a single global counter; per-session rings only
    // contain events for that session. Asking the new session for `since=<old
    // session's max id>` skips all of the new session's events whose ids are
    // numerically lower — the SPA then sees nothing for the in-flight turn.
    const sessionChanged = lastSessionRef.current !== activeSessionId;
    lastSessionRef.current = activeSessionId;
    const store = useRootStore;
    if (sessionChanged) {
      store.getState().setLastEventId(null);
    }
    // MED-4 polish — centralised via `getDefaultApiContext()` so the SSE
    // stream and downstream API calls agree on the same bearer-token source
    // (`localStorage.tessaLeanToken`).
    const ctxToken = getDefaultApiContext().token;
    const url = (buildUrl ?? defaultBuildSseUrl)(activeSessionId);
    const since = store.getState().lastEventId;

    store.getState().setCurrentSession(activeSessionId);
    store.getState().beginConnect(activeSessionId);

    handleRef.current = openSseStream({
      url,
      token: ctxToken,
      since,
      fetchEventSourceImpl,
      onOpen: () => {
        store.getState().markOpen();
        // Successful open → reset the SPA-level reconnect counter so the
        // next blip starts fresh.
        reconnectAttemptsRef.current = 0;
      },
      onError: () => {
        store.getState().markReconnecting();
        // Mark current handle as dead so the re-run's "already open" check
        // doesn't short-circuit — without this, the bumped reconnectToken
        // triggers the effect but the open path is skipped.
        handleRef.current = null;
        // Cap reconnect attempts. Without this, the bump-token loop spawns
        // fresh fetch-event-source instances indefinitely whenever the
        // server is unreachable (each has its own retry budget).
        if (reconnectAttemptsRef.current >= MAX_SPA_RECONNECTS) {
          store.getState().markDisconnected();
          // eslint-disable-next-line no-console
          console.warn(
            `[SseProvider] reconnect cap hit (${MAX_SPA_RECONNECTS}) — giving up. Reload the page to retry.`,
          );
          return;
        }
        reconnectAttemptsRef.current += 1;
        // eslint-disable-next-line no-console
        console.info(
          `[SseProvider] SSE closed — scheduling reconnect ${reconnectAttemptsRef.current}/${MAX_SPA_RECONNECTS} in 500ms`,
        );
        // Schedule a re-open via reconnectToken bump. Guard against multiple
        // bumps from a single error storm — clear any pending timer first.
        if (reconnectTimerRef.current !== null) {
          clearTimeout(reconnectTimerRef.current);
        }
        reconnectTimerRef.current = setTimeout(() => {
          reconnectTimerRef.current = null;
          setReconnectToken((t) => t + 1);
        }, 500);
      },
      onExhausted: () => {
        store.getState().markDisconnected();
      },
      onEvent: (event: SseEvent) => {
        // The slice subscribes to `activeSessionId` for routing scoping.
        dispatchSseEvent(
          store as unknown as Parameters<typeof dispatchSseEvent>[0],
          event,
          {
            getActiveSessionId: () => store.getState().activeSessionId,
          },
        );
      },
    });

    return () => {
      handleRef.current?.abort();
      handleRef.current = null;
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
  }, [activeSessionId, buildUrl, fetchEventSourceImpl, reconnectToken]);

  return <>{children}</>;
}
